import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';

import { useCalendarUI } from '../../context/CalendarUIContext';
import { useEventFormLogic } from '../../hooks/useEventFormLogic';
import { ModalHeader } from 'shared/ModalHeader';
import { EventForm } from './FormComponents';
import { useCalendarState } from 'modules/calendarv2/context/CalendarContext';

/**
 * EventFormModal - Main modal component for creating and editing events
 *
 * This component provides a clean, professional modal interface for event management.
 * It uses a modular component structure with separation of concerns:
 * - Business logic is in useEventFormLogic hook
 * - UI components are in separate EventForm with field components
 */
const EventFormModal = () => {
  const { modals } = useCalendarUI();
  const { isGlobal } = useCalendarState();
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    resetField,
    errors,
    onSubmit,
    isSubmitting,
    eventFormMode,
    closeEventFormModal,
    uploadProgress,
    isUploading,
    attendeesList,
    isLoadingAttendees,
  } = useEventFormLogic();

  const footer = (
    <div className="footer-btn d-flex justify-content-between align-items-center">
      {/* Upload Progress - Left Side */}
      <div className="d-flex">
        {isUploading && (
          <div className="d-flex align-items-center me-3">
            <small className="text-muted me-2">Uploading files...</small>
            <ProgressBar value={uploadProgress} style={{ width: '100px', height: '6px' }} showValue={false} />
            <small className="text-muted ms-2">{uploadProgress}%</small>
          </div>
        )}
      </div>

      {/* Action Buttons - Right Side */}
      <div className="d-flex">
        <Button label="Cancel" className="btn p-button-secondary outline me-2" onClick={closeEventFormModal} disabled={isSubmitting} />
        <Button
          label={eventFormMode === 'create' ? 'Save' : 'Update'}
          type="button"
          className="p-button-secondary"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting || isUploading}
          icon={isSubmitting ? 'pi pi-spin pi-spinner' : undefined}
        />
      </div>
    </div>
  );

  return (
    <Dialog
      visible={modals.eventForm}
      modal
      onHide={closeEventFormModal}
      className="new-task-popup media-popup"
      header={ModalHeader}
      resizable={false}
      footer={footer}
    >
      <EventForm
        control={control}
        resetField={resetField}
        setValue={setValue}
        getValues={getValues}
        watch={watch}
        errors={errors}
        trigger={trigger}
        eventFormMode={eventFormMode}
        isGlobal={isGlobal}
        attendeesList={attendeesList}
        isLoadingAttendees={isLoadingAttendees}
      />
    </Dialog>
  );
};

export default EventFormModal;
